import { EVIDENCE_CONFIDENCE, PROVENANCE, takesAnswers, type Provenance } from '@cyberscore/contracts';
import { FileCheck, FileSearch, Layers, Scale } from 'lucide-react';
import { useState } from 'react';
import { href } from '../../lib/router';
import { useToast } from '../../ui/Toasts';
import { useCanAnswer } from '../assessments/api';
import { DocumentRow, DropZone, uploadError } from '../documents/DocumentParts';
import { useDocuments, useSuggestions, useUpload } from '../documents/api';
import { reviewGroups } from '../documents/files';
import { Caps } from './charts';
import { useBusiness, useBusinessScore } from './context';
import { CardHead } from './parts';
import { PROVENANCE_LABEL } from './progress';
import { NoAssessment, ScoreProblem } from './states';

/** The confidence ladder, one rung per level of proof, lowest first. */
const LADDER = (() => {
  const seen = new Map<number, Provenance[]>();
  for (const p of PROVENANCE) seen.set(EVIDENCE_CONFIDENCE[p], [...(seen.get(EVIDENCE_CONFIDENCE[p]) ?? []), p]);
  return [...seen.entries()].sort((a, b) => a[0] - b[0]).map(([v, ps]) => ({ v: Math.round(v * 100), label: PROVENANCE_LABEL[ps[0]!] }));
})(); // prettier-ignore

type Filter = 'verified' | 'declared' | 'all';

/**
 * Evidence, as the prototype lays it out: declared against verified, what each kind of proof is worth,
 * and the locker: every answered protection with how it is known. Documents are uploaded here too.
 */
export function LockerPage() {
  const { loading } = useBusiness();
  const { current: a, view } = useBusinessScore();
  const [filter, setFilter] = useState<Filter | null>(null);
  if (loading) return <div className="empty">Loading…</div>;
  if (!a) return <NoAssessment />;
  if (view.isPending) return <div className="empty">Loading…</div>;
  if (view.isError) return <ScoreProblem a={a} error={view.error} />;

  const { score: r, explanation: trace } = view.data;
  const answered = trace.controls.filter((c) => c.status !== 'unanswered' && c.provenance);
  const verified = answered.filter((c) => c.confidence > EVIDENCE_CONFIDENCE.USER_DECLARED);
  const show = filter ?? (verified.length ? 'verified' : 'all');
  const rows = (show === 'verified' ? verified : show === 'declared' ? answered.filter((c) => c.confidence <= EVIDENCE_CONFIDENCE.USER_DECLARED) : answered)
    .slice()
    .sort((x, y) => y.confidence - x.confidence || (x.control < y.control ? -1 : 1));
  const declared = Math.round(r.controls.declared);
  const effective = Math.round(r.controls.effective);
  const L = (v: number) => 4 + (v / 100) * 92;

  return (
    <>
      <div className="bpage-head">
        <div>
          <h1>Evidence</h1>
          <p>Every answer carries a confidence level. Upload documents or connect systems to move from “we say so” to “we can show it”.</p>
        </div>
      </div>
      <div className="bgrid g-side">
        <section className="bcard">
          <CardHead tone="warn" icon={<Scale size={18} />} title="Declared vs verified" sub="Your protections, weighted, as you told us and after evidence" />
          <div className="gap-vis">
            <div className="track" />
            <div className="gapfill" style={{ left: `${L(effective)}%`, width: `${Math.max(0, L(declared) - L(effective))}%` }} />
            <div className="mk below" style={{ left: `${L(effective)}%`, color: 'var(--warn)' }}>Verified {effective}</div>
            <div className="mk" style={{ left: `${L(declared)}%`, color: 'var(--primary)' }}>Declared {declared}</div>
          </div>
          <div className="row-between muted tiny"><span>0</span><span>50</span><span>100</span></div>
          <p className="mt measure">
            Unverified answers get partial credit: <span className="mono">credit = declared × (0.75 + 0.25 × confidence)</span>. Closing the gap is often the fastest way to raise your score and your insurer’s trust.
          </p>
        </section>
        <section className="bcard">
          <CardHead icon={<Layers size={18} />} title="Evidence confidence" sub="What each kind of proof is worth" />
          <div className="ladder">
            {LADDER.map((l) => (
              <div key={l.v} className="rung">
                <span>{l.label}</span>
                <span className={`bar ${l.v >= 75 ? 'ok' : l.v >= 40 ? '' : 'warn'}`}><span style={{ width: `${l.v}%` }} /></span>
                <b className="num">{l.v}%</b>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="bgrid g-side-r mt-l">
        <section className="bcard flush">
          <div className="pad-h">
            <CardHead
              icon={<FileCheck size={18} />}
              title="Evidence locker"
              sub={`${answered.length} answered protections · ${verified.length} backed by more than your word`}
              end={
                <div className="bseg" role="tablist" aria-label="Show">
                  {(['verified', 'declared', 'all'] as const).map((f) => (
                    <button key={f} type="button" role="tab" aria-selected={show === f} onClick={() => setFilter(f)}>
                      {f === 'verified' ? 'Verified' : f === 'declared' ? 'Self-declared' : 'All'}
                    </button>
                  ))}
                </div>
              }
            />
          </div>
          <div className="btbl-wrap">
            <table className="btbl">
              <thead>
                <tr><th>Protection</th><th>How we know</th><th>Confidence</th><th>Status</th></tr>
              </thead>
              <tbody>
                {rows.map((c) => {
                  const conf = Math.round(c.confidence * 100);
                  return (
                    <tr key={c.control}>
                      <td><b>{c.name}</b><div className="muted tiny mono">{c.control}</div></td>
                      <td className="nowrap">{PROVENANCE_LABEL[c.provenance!]}</td>
                      <td><span className="row gap-s"><Caps value={conf} n={10} tone={conf >= 75 ? 'ok' : conf >= 40 ? '' : 'warn'} small /><span className="num">{conf}%</span></span></td>
                      <td><span className={`bbadge ${c.status === 'in_place' ? 'ok' : c.status === 'missing' ? 'bad' : c.status === 'partial' ? 'warn' : ''}`}>{c.status === 'in_place' ? 'In place' : c.status === 'partial' ? 'Partly' : c.status === 'missing' ? 'Missing' : 'Not sure'}</span></td>
                    </tr>
                  );
                })}
                {!rows.length && (
                  <tr><td colSpan={4} className="muted">{show === 'verified' ? 'Nothing verified yet. Upload a document or connect a system.' : 'Nothing here yet.'}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
        <Documents id={a.id} open={takesAnswers(a.status)} />
      </div>
    </>
  );
} // prettier-ignore

function Documents({ id, open }: { id: string; open: boolean }) {
  const docs = useDocuments(id);
  const suggestions = useSuggestions(id);
  const upload = useUpload(id);
  const canAnswer = useCanAnswer();
  const toast = useToast();
  const g = reviewGroups(suggestions.data ?? []);
  return (
    <section className="bcard">
      <CardHead icon={<FileSearch size={18} />} title="Your documents" sub="Policies, screenshots and reports, read for the answers they give" />
      {open && canAnswer && (
        <DropZone
          busy={upload.isPending}
          label="Drop policies, screenshots or reports"
          onFile={async (file) => {
            try {
              const res = await upload.mutateAsync({ file });
              toast(res.duplicate ? `${file.name} was already uploaded` : `${file.name} uploaded. Reading it now.`, 'ok');
            } catch (e) {
              toast(uploadError(e), 'bad');
            }
          }}
        />
      )}
      {g.total > 0 && (
        <a className="bnote warn mt block-link" href={href.documents(id)}>
          {g.total} {g.total === 1 ? 'answer' : 'answers'} from your documents to review
          {g.conflicts.length > 0 && `, ${g.conflicts.length} where they disagree`}
        </a>
      )}
      {docs.data?.length ? (
        <ul className="doc-list mt">
          {docs.data.slice(0, 6).map((d) => <DocumentRow key={d.id} d={d} />)}
        </ul>
      ) : (
        <p className="muted small mt">{docs.isPending ? 'Loading…' : 'No documents yet.'}</p>
      )}
      <a className="link-btn mt" href={href.documents(id)}>All documents and what they say</a>
    </section>
  );
} // prettier-ignore
